import { routeRolloutRegistry } from '@usealloy/daffy-to-frontend-dashboard-rollout';
import { generatePath, matchPath } from 'react-router-dom';

import type { BetaKey } from './constants';

/**
 * TODO(FRONTEND-680): REMOVE once David's FD counterpart lands in
 * `@usealloy/daffy-to-frontend-dashboard-rollout` (match/convert helpers). (FRONTEND-608, the
 * design-decision epic this shim originally cited, closed without that jslib work landing —
 * FRONTEND-680 is the follow-up ticket that owns actually removing it.)
 * Delete this file and switch imports to the jslib; do not extend this shim further.
 *
 * Temporary local path helpers over `routeRolloutRegistry` so we consume David's path pairs
 * without owning match/convert utilities he is actively drafting.
 */

type RolloutSide = 'daffy' | 'frontendDashboard';

function candidatesForPathname(pathname: string): string[] {
  const candidates = [pathname];
  if (pathname.length > 1 && pathname.endsWith('/')) {
    candidates.push(pathname.slice(0, -1));
  } else if (pathname.length > 1) {
    candidates.push(`${pathname}/`);
  }
  return candidates;
}

function otherSide(side: RolloutSide): RolloutSide {
  return side === 'daffy' ? 'frontendDashboard' : 'daffy';
}

/**
 * Core lookup shared by all four exported helpers below: finds the registry path pair whose
 * `from` side matches `pathname` and returns its `to` side with params filled in, or null if
 * no group (optionally scoped to `cookie`) has a match.
 */
function findRegisteredPath(
  pathname: string,
  from: RolloutSide,
  cookie?: BetaKey,
): string | null {
  const to = otherSide(from);
  const candidates = candidatesForPathname(pathname);
  const groups = cookie === undefined
    ? routeRolloutRegistry
    : routeRolloutRegistry.filter(group => group.cookie === cookie);

  for (const path of groups.flatMap(group => group.paths)) {
    // A net-new page has no `daffy` counterpart; a `from: 'daffy'` lookup can never match it.
    if (path[from] && path[to]) {
      for (const candidate of candidates) {
        const match = matchPath(path[from], candidate);
        if (match) {
          return generatePath(path[to], match.params);
        }
      }
    }
  }
  return null;
}

/** Map a Daffy path to its registered `/beta/...` path, or null if unregistered. */
export function toFrontendDashboardPath(daffyPathname: string): string | null {
  return findRegisteredPath(daffyPathname, 'daffy');
}

/**
 * Map a `/beta/...` path to Daffy. Registered paths use the registry; everything else gets a
 * simple `/v3/dashboard/...` prefix.
 */
export function toDaffyPath(frontendDashboardPathname: string): string {
  const registered = findRegisteredPath(frontendDashboardPathname, 'frontendDashboard');
  if (registered) {
    return registered;
  }

  const stripped = frontendDashboardPathname.replace(/^\/beta\/?/, '');
  if (stripped === 'v3/dashboard' || stripped.startsWith('v3/dashboard/')) {
    return `/${stripped}`;
  }
  if (stripped === '') {
    return '/v3/dashboard';
  }
  return `/v3/dashboard/${stripped}`;
}

/**
 * Cookie-scoped counterpart to `toFrontendDashboardPath`: only matches groups whose `cookie`
 * is `key`, so enabling one area never maps a path that belongs to a different one. Used by
 * `resolveNavAction`; `toFrontendDashboardPath` (search every group) stays for SideNav.
 */
export function toFrontendDashboardPathForCookie(
  daffyPathname: string,
  key: BetaKey,
): string | null {
  return findRegisteredPath(daffyPathname, 'daffy', key);
}

/**
 * Cookie-scoped counterpart to `toDaffyPath`: only matches groups whose `cookie` is `key`, and
 * returns null (rather than falling back to a computed `/v3/dashboard/...` path) when the
 * current page isn't one of that area's registered paths -- the caller treats null as "nothing
 * to map back to" and stays put instead of guessing a legacy URL for a different area's page.
 */
export function toDaffyPathForCookie(
  frontendDashboardPathname: string,
  key: BetaKey,
): string | null {
  return findRegisteredPath(frontendDashboardPathname, 'frontendDashboard', key);
}
